import React, { useCallback, useRef } from "react";
import { useFormikContext } from "formik";
import Scrollbars from "react-custom-scrollbars-2";
import classNames from "classnames";
import {
    StandardModalBody,
    StandardModalHeader,
} from "../../general/Modal/Modal";
import { EditableTable } from "../../accounting/editableTable/EditableTableContext";
import { useFinancialAccounts } from "../../../hooks/useFinancialAccounts";
import { AccountsDropdown } from "../../general/AccountsFilter/AccountsDropdown";
import styles from "./AddTransactionsModal.module.scss";
import {
    AddTransactionsModalFormikConfig,
    TransactionProxyObject,
} from "./AddTransactionsModal";
import { Footer } from "./Table/Footer";
import { EditableTableHeader } from "./Table/Header";
import { Body } from "./Table/Body";
import { useUpdateAndReturnNewLineCallback } from "./hooks";

interface Props {
    loading: boolean;
}

export const AddTransactionsModalContent: React.FC<Props> = ({ loading }) => {
    const scrollbarsRef = useRef<Scrollbars>(null);
    const tableRef = useRef<HTMLDivElement>(null);

    const formikContext = useFormikContext<AddTransactionsModalFormikConfig>();
    const { values, setFieldValue } = formikContext;

    const transactions = values.transactions;

    const linesRef = React.useRef(transactions);

    React.useEffect(() => {
        linesRef.current = transactions;
    }, [transactions]);

    const updateAndReturnNewLine = useUpdateAndReturnNewLineCallback();

    const addLines = useCallback(() => {
        const currentLines = linesRef.current;
        const newLines = [
            ...currentLines,
            ...new Array(5).fill(null).map(() => ({
                id: Math.random().toString(),
                accountCode: "",
                debitAmount: null,
                creditAmount: null,
                description: "",
            })),
        ];
        setFieldValue(`transactions`, newLines);
    }, [linesRef, setFieldValue]);

    const bankAccounts = useFinancialAccounts({ createTempCashAccounts: true });

    return (
        <EditableTable<TransactionProxyObject>
            updateAndReturnNewLine={updateAndReturnNewLine}
            targetLines={transactions}
        >
            <>
                <StandardModalHeader>Add transaction</StandardModalHeader>
                <StandardModalBody className={styles.modalBody}>
                    <div
                        className={classNames(
                            "p-4 gap-2 d-flex",
                            styles.modalBodyHeader,
                        )}
                    >
                        <AccountsDropdown
                            onChange={(accountIds) => {
                                setFieldValue(
                                    "financialAccountId",
                                    accountIds[0],
                                );
                                setFieldValue(
                                    "entityId",
                                    bankAccounts.find(
                                        ({ id }) => id === accountIds[0],
                                    )?.entityId,
                                );
                            }}
                            accountIds={
                                values.financialAccountId
                                    ? [values.financialAccountId]
                                    : []
                            }
                            isSingleSelect
                            createTempCashAccounts
                            excludeNonManualAccounts
                        />
                    </div>
                    <EditableTableHeader />

                    <Scrollbars
                        ref={scrollbarsRef}
                        className={styles.scrollbars}
                    >
                        <Body
                            setFieldValue={setFieldValue}
                            tableRef={tableRef}
                            scrollbarsRef={scrollbarsRef}
                        />
                    </Scrollbars>
                </StandardModalBody>

                <Footer loading={loading} addLines={addLines} />
            </>
        </EditableTable>
    );
};
